<template>
  <div>
    <section
      class="card"
      aria-label="Meldezettel Export"
    >
      <div class="form">
        <div class="field">
          <label for="from">Datum von</label>
          <FlatPickr
            id="from"
            v-model="form.from"
            :config="dateOnlyConfig"
            class="flatpickr-date"
            :placeholder="'TT.MM.JJJJ'"
          />
        </div>
        <div class="field">
          <label for="to">Datum bis</label>
          <FlatPickr
            id="to"
            v-model="form.to"
            :config="dateOnlyConfig"
            class="flatpickr-date"
            :placeholder="'TT.MM.JJJJ'"
          />
        </div>
        <div class="field actions">
          <button
            class="btn btn-primary"
            :disabled="exporting || !form.from || !form.to"
            title="PDF erstellen"
            @click="onCreatePdf"
          >
            <span
              v-if="exporting"
              class="spinner"
              aria-hidden="true"
            />
            Erstellen
          </button>
        </div>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>
    </section>

    <!-- Viewer Overlay -->
    <div
      v-if="viewer.open"
      class="viewer-overlay"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @keydown.esc="closeViewer"
    >
      <div class="viewer">
        <div class="viewer-header">
          <span class="viewer-title">{{ viewer.fileName }}</span>
          <div class="viewer-actions">
            <button
              class="btn"
              :disabled="!viewer.url"
              @click="printPdf"
            >
              Drucken
            </button>
            <button
              class="btn btn-primary"
              :disabled="!viewer.url"
              @click="downloadPdf"
            >
              Download
            </button>
            <button
              class="btn"
              title="Schließen"
              @click="closeViewer"
            >
              Schließen
            </button>
          </div>
        </div>
        <div class="viewer-body">
          <iframe
            v-if="viewer.url"
            :src="viewer.url"
            class="viewer-frame"
            title="PDF"
          />
          <div
            v-else
            class="viewer-loading"
          >
            Lädt …
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import api from '@/services/api';
import { jsPDF } from 'jspdf';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { German } from 'flatpickr/dist/l10n/de.js';

const exporting = ref(false);
const error = ref('');

const form = ref({
  from: '',
  to: ''
});

// FlatPickr: Datum (ohne Zeit) wie im Dialog
const dateOnlyConfig = {
  enableTime: false,
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
};

// Viewer
const viewer = ref({
  open: false,
  url: null,
  fileName: 'Meldezettel.pdf'
});

function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '-';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function fmtTime(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^(\d{2}):(\d{2})/.test(v)) {
    const m = v.match(/^(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : '';
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
}

function isSameYMD(a, b) {
  const da = a ? new Date(a) : null;
  const db = b ? new Date(b) : null;
  if (!da || !db || Number.isNaN(da) || Number.isNaN(db)) return false;
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
}

function buildRange(n) {
  const from = n.dateFrom ?? n.date_from ?? null;
  const to = n.dateTo ?? n.date_to ?? null;
  const tFrom = n.timeFrom ?? n.time_from ?? null;
  const tTo = n.timeTo ?? n.time_to ?? null;

  if (from && to && isSameYMD(from, to) && (tFrom || tTo)) {
    const d = fmtDate(from);
    const tf = fmtTime(tFrom);
    const tt = fmtTime(tTo);
    if (tf && tt) return `${d} ${tf}–${tt}`;
    if (tf) return `${d} ab ${tf}`;
    if (tt) return `${d} bis ${tt}`;
    return d;
  }
  if (from && to) {
    if (isSameYMD(from, to)) return fmtDate(from);
    return `${fmtDate(from)} — ${fmtDate(to)}`;
  }
  if (from) return fmtDate(from);
  if (to) return fmtDate(to);
  return '—';
}

async function onCreatePdf() {
  error.value = '';
  exporting.value = true;
  try {
    const params = { status: 'akzeptiert', from: form.value.from, to: form.value.to };
    const res = await api.get('/notifications', { params });
    const raw = res?.data;
    const list = Array.isArray(raw) ? raw
      : Array.isArray(raw?.items) ? raw.items
        : (raw && typeof raw === 'object' && raw.id ? [raw] : []);

    // Fallback-Filter clientseitig (Status + Datum)
    const fromDate = new Date(form.value.from);
    const toDate = new Date(form.value.to);
    toDate.setHours(23, 59, 59, 999);

    const items = (list || [])
      .filter(n => String((n.status ?? n.state) || '').toLowerCase() === 'akzeptiert')
      .filter(n => {
        const d = n.createdAt ?? n.created_at ?? n.sentAt ?? n.sent_at ?? n.dateFrom ?? n.date_from;
        const dt = d ? new Date(d) : null;
        if (!dt || Number.isNaN(dt.getTime())) return false;
        return dt >= fromDate && dt <= toDate;
      })
      .sort((a, b) => {
        const ad = new Date((a.createdAt ?? a.created_at ?? a.sentAt ?? a.sent_at ?? a.dateFrom ?? a.date_from) ?? 0).getTime();
        const bd = new Date((b.createdAt ?? b.created_at ?? b.sentAt ?? b.sent_at ?? b.dateFrom ?? b.date_from) ?? 0).getTime();
        return ad - bd;
      });

    const { base64, fileName } = buildReportPdf(items, form.value.from, form.value.to);

    // Blob-URL erstellen und im Viewer öffnen
    const byteString = atob(base64);
    const len = byteString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    viewer.value.url = url;
    viewer.value.fileName = fileName;
    viewer.value.open = true;
  } catch (e) {
    error.value = 'PDF konnte nicht erstellt werden.';
  } finally {
    exporting.value = false;
  }
}

function buildReportPdf(items, from, to) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const todayText = `${dd}.${mm}.${yyyy}`;

  function nameLastFirst(n) {
    const rawFirst = n.studentFirstName ?? n.student_first_name ?? n.firstName ?? n.first_name ?? '';
    const rawLast = n.studentLastName ?? n.student_last_name ?? n.lastName ?? n.last_name ?? '';
    let firstName = String(rawFirst || '').trim();
    let lastName = String(rawLast || '').trim();
    if (!firstName || !lastName) {
      const s = String(n.studentName ?? n.student_name ?? '').trim();
      if (s.includes(',')) {
        const parts = s.split(',').map(p => p.trim());
        lastName = lastName || (parts[0] || '');
        firstName = firstName || (parts[1] || '');
      } else if (s) {
        const parts = s.split(/\s+/);
        if (parts.length >= 2) {
          firstName = firstName || parts.slice(0, -1).join(' ');
          lastName = lastName || parts.slice(-1)[0];
        } else {
          firstName = firstName || s;
        }
      }
    }
    const ordered = [lastName, firstName].filter(Boolean).join(', ');
    return ordered || (n.studentName ?? n.student_name ?? '—');
  }

  function drawPage(n, isFirst) {
    if (!isFirst) doc.addPage();

    const left = 20;
    let y = 22;

    const nameOrdered = nameLastFirst(n);
    const group = n.groupName ?? n.studentGroup ?? n.className ?? n.class ?? n.group ?? '';
    const fromD = n.dateFrom ?? n.date_from ?? null;
    const toD = n.dateTo ?? n.date_to ?? null;
    const tFrom = n.timeFrom ?? n.time_from ?? null;
    const tTo = n.timeTo ?? n.time_to ?? null;

    const isHours = Boolean(fromD && toD && isSameYMD(fromD, toD) && (tFrom || tTo));
    const isMulti = Boolean(fromD && toD && !isSameYMD(fromD, toD));
    const isFull = Boolean(
      (fromD && toD && isSameYMD(fromD, toD) && !isHours) ||
      (fromD && !toD && !tFrom && !tTo)
    );

    const subjText = String((n.subject ?? n.subject_text ?? '') || '').trim();
    const commentText = String((n.comment ?? n.comment_text ?? n.message ?? n.text ?? n.body ?? '') || '').trim();
    const dateText = buildRange(n) || '—';

    // Titel
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Antrag auf Entschuldigung', left, y);
    y += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    // Name, Vorname → „Nachname, Vorname“
    doc.text('Name, Vorname:', left, y);
    doc.setFont('helvetica', 'bold');
    doc.text(String(nameOrdered), left + 45, y);
    doc.setFont('helvetica', 'normal');
    y += 10;

    // Lerngruppe
    doc.text('Lerngruppe:', left, y);
    doc.setFont('helvetica', 'bold');
    doc.text(String(group || '—'), left + 45, y);
    doc.setFont('helvetica', 'normal');
    y += 10;

    // Datum der Fehlzeit
    doc.text('Datum der Fehlzeit:', left, y);
    doc.setFont('helvetica', 'bold');
    doc.text(String(dateText), left + 45, y);
    doc.setFont('helvetica', 'normal');
    y += 14;

    // Auswahlfelder
    const box = (x, yy, checked, label) => {
      const s = 5;
      doc.rect(x, yy - 4.5, s, s);
      if (checked) {
        doc.setFont('helvetica', 'bold');
        doc.text('X', x + 1.6, yy - 0.8);
        doc.setFont('helvetica', 'normal');
      }
      doc.text(label, x + s + 3, yy);
    };
    box(left, y, isFull, 'Ganztägig');
    box(left + 50, y, isMulti, 'Mehrtägig');
    box(left + 105, y, isHours, 'Stundenweise');
    y += 16;

    // Grund
    doc.setFont('helvetica', 'normal');
    doc.text('Grund:', left, y);

    const maxWidth = 170;
    const labelOffset = 45;
    const lineHeight = 6;

    // Subject fett rechts neben "Grund:"
    const subjectLines = subjText ? doc.splitTextToSize(subjText, maxWidth - labelOffset) : [];
    if (subjectLines.length) {
      doc.setFont('helvetica', 'bold');
      doc.text(subjectLines, left + labelOffset, y);
      doc.setFont('helvetica', 'normal');
    }
    const subjBlockHeight = (subjectLines.length ? subjectLines.length : 1) * lineHeight;
    y += subjBlockHeight + 2;

    // Kommentar kleiner darunter
    if (commentText) {
      doc.setFontSize(10);
      const commentLines = doc.splitTextToSize(commentText, maxWidth - labelOffset);
      doc.text(commentLines, left + labelOffset, y);
      y += Math.max(1, commentLines.length) * lineHeight;
      doc.setFontSize(12);
    } else {
      y += 6;
    }

    y += 8;

    // Ort, Datum / Unterschrift
    doc.text('Ort, Datum:', left, y);
    doc.setFont('helvetica', 'bold');
    doc.text(todayText, left + 30, y);
    doc.setFont('helvetica', 'normal');

    const signX = left + 95;
    doc.text('Unterschrift:', signX, y);
    doc.setDrawColor(224, 227, 234);
    doc.line(signX + 28, y + 1, left + maxWidth, y + 1);

    // Hinweis
    y += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.text('Dieses Schreiben wurde maschinell erstellt und ist ohne Unterschrift gültig', left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
  }

  items.forEach((n, idx) => drawPage(n, idx === 0));

  const fileName = `Meldezettel_Sammel_${from}_${to}.pdf`;
  const dataUri = doc.output('datauristring');
  const base64 = String(dataUri).split(',')[1] || '';
  return { base64, fileName };
}

function closeViewer() {
  try {
    if (viewer.value.url) URL.revokeObjectURL(viewer.value.url);
  } catch { /* ignore */ }
  viewer.value.url = null;
  viewer.value.open = false;
}

function downloadPdf() {
  if (!viewer.value.url) return;
  const a = document.createElement('a');
  a.href = viewer.value.url;
  a.download = viewer.value.fileName || 'export.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function printPdf() {
  if (!viewer.value.url) return;
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  iframe.src = viewer.value.url;
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch { /* ignore */ }
    setTimeout(() => iframe.remove(), 2000);
  };
}
</script>

<style scoped>
.card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 14px;
}

.form {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 240px));
  gap: 12px;
  align-items: end;
}
.field { display: flex; flex-direction: column; gap: 6px; }
label { font-size: 12px; color: var(--muted); }

:deep(input.flatpickr-date) {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
  width: 240px;
}
:deep(input.flatpickr-date::placeholder) { color: #c9cdd3; }

.actions { align-items: flex-start; }

.spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.6);
  border-top-color: #fff; display: inline-block; margin-right: 6px;
  animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}
.notice-danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
  color: #7f1d1d;
}

.viewer-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  display: flex; align-items: center; justify-content: center; z-index: 40;
}
.viewer {
  width: min(100%, 960px); max-height: 90vh; background: var(--surface);
  border: 1px solid var(--border); border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.15);
  display: flex; flex-direction: column; overflow: hidden;
}
.viewer-header {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 10px 12px; border-bottom: 1px solid var(--border); background: var(--surface);
}
.viewer-actions { display: inline-flex; gap: 8px; }
.viewer-body {
  position: relative; background: var(--bg);
  display: flex; align-items: center; justify-content: center;
  min-height: 360px; height: 70vh; padding: 12px;
}
.viewer-frame { width: 100%; height: 100%; border: 0; background: var(--surface); border-radius: 6px; }
.mt-12 { margin-top: 12px; }
</style>
